<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import InboxesAPI from '../../../../../api/inboxes';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  inboxId: {
    type: [String, Number],
    required: true,
  },
});

const { t } = useI18n();

const isLoading = ref(true);
const data = ref(null);
const errorMessage = ref(null);

const fetchQualityIndex = async () => {
  try {
    isLoading.value = true;
    errorMessage.value = null;
    const response = await InboxesAPI.getQualityIndex(props.inboxId);
    data.value = response.data;
  } catch (error) {
    data.value = null;
    errorMessage.value = t('INBOX_MGMT.QUALITY_INDEX.ERROR');
  } finally {
    isLoading.value = false;
  }
};

const retry = () => fetchQualityIndex();

onMounted(() => {
  fetchQualityIndex();
});

const getBadgeColor = status => {
  switch (status) {
    case 'good':
      return 'bg-n-teal-3 text-n-teal-11';
    case 'attention':
      return 'bg-n-amber-3 text-n-amber-11';
    case 'risk':
      return 'bg-n-ruby-3 text-n-ruby-11';
    default:
      return 'bg-n-slate-3 text-n-slate-11';
  }
};

const getBadgeLabel = status => {
  switch (status) {
    case 'good':
      return t('INBOX_MGMT.QUALITY_INDEX.STATUS.GOOD');
    case 'attention':
      return t('INBOX_MGMT.QUALITY_INDEX.STATUS.ATTENTION');
    case 'risk':
      return t('INBOX_MGMT.QUALITY_INDEX.STATUS.RISK');
    default:
      return t('INBOX_MGMT.QUALITY_INDEX.STATUS.UNKNOWN');
  }
};
</script>

<template>
  <div
    class="mt-6 px-5 py-5 space-y-6 rounded-xl outline outline-1 -outline-offset-1 outline-n-weak bg-n-solid-2"
  >
    <div class="flex flex-col gap-2">
      <h3 class="text-heading-3 text-n-slate-12 flex items-center gap-2">
        <Icon icon="i-lucide-activity" class="w-5 h-5 text-n-slate-11" />
        {{ t('INBOX_MGMT.QUALITY_INDEX.TITLE') }}
      </h3>
      <p class="text-body-main text-n-slate-11">
        {{ t('INBOX_MGMT.QUALITY_INDEX.DESCRIPTION') }}
      </p>
    </div>

    <div v-if="isLoading" class="flex items-center justify-center py-10">
      <div class="i-lucide-loader-2 w-8 h-8 animate-spin text-n-slate-8" />
    </div>

    <div
      v-else-if="errorMessage"
      class="flex flex-col items-center gap-3 py-10 text-center"
    >
      <p class="text-body-main text-n-slate-11">{{ errorMessage }}</p>
      <button
        type="button"
        class="text-n-blue-11 hover:underline"
        @click="retry"
      >
        {{ t('INBOX_MGMT.QUALITY_INDEX.RETRY') }}
      </button>
    </div>

    <div
      v-else-if="data && !data.available"
      class="flex flex-col items-center gap-2 py-10 text-center"
    >
      <Icon icon="i-lucide-bar-chart-3" class="w-8 h-8 text-n-slate-9" />
      <p class="text-body-main text-n-slate-11">
        {{ t('INBOX_MGMT.QUALITY_INDEX.NO_DATA') }}
      </p>
    </div>

    <div v-else-if="data" class="flex flex-col md:flex-row gap-6">
      <!-- Score Column -->
      <div
        class="flex-shrink-0 flex flex-col items-center justify-center p-6 bg-n-solid-1 rounded-xl border border-n-weak md:w-1/3"
      >
        <span
          class="text-[48px] leading-tight font-bold"
          :class="[
            data.score >= 80 ? 'text-n-teal-11' : '',
            data.score >= 60 && data.score < 80 ? 'text-n-amber-11' : '',
            data.score < 60 ? 'text-n-ruby-11' : '',
          ]"
        >
          {{ data.score
          }}<span class="text-heading-3 text-n-slate-9">{{
            t('INBOX_MGMT.QUALITY_INDEX.OUT_OF')
          }}</span>
        </span>
        <span class="text-label text-n-slate-11 uppercase font-medium mt-1">
          {{ t('INBOX_MGMT.QUALITY_INDEX.SCORE_LABEL') }}
        </span>

        <!-- Progress Bar -->
        <div
          class="w-full h-2 mt-4 bg-n-slate-3 rounded-full overflow-hidden relative"
        >
          <div
            class="h-full rounded-full transition-all duration-1000"
            :class="[
              data.score >= 80 ? 'bg-n-teal-9' : '',
              data.score >= 60 && data.score < 80 ? 'bg-n-amber-9' : '',
              data.score < 60 ? 'bg-n-ruby-9' : '',
            ]"
            :style="{ width: `${data.score}%` }"
          />
        </div>
      </div>

      <!-- Metrics Column -->
      <div class="flex-1 space-y-3">
        <!-- Interação -->
        <div
          class="flex justify-between items-center p-4 bg-n-solid-1 border border-n-weak rounded-lg"
        >
          <div>
            <h4 class="text-body-main font-semibold text-n-slate-12">
              {{ t('INBOX_MGMT.QUALITY_INDEX.INTERACTION') }}
            </h4>
            <p class="text-label text-n-slate-11 mt-0.5">
              {{ data.interaction.label }}
            </p>
          </div>
          <span
            class="px-2 py-1 rounded-md text-xs font-semibold tracking-wide uppercase"
            :class="getBadgeColor(data.interaction.status)"
          >
            {{ getBadgeLabel(data.interaction.status) }}
          </span>
        </div>

        <!-- Ritmo de Envio -->
        <div
          class="flex justify-between items-center p-4 bg-n-solid-1 border border-n-weak rounded-lg"
        >
          <div>
            <h4 class="text-body-main font-semibold text-n-slate-12">
              {{ t('INBOX_MGMT.QUALITY_INDEX.PACE') }}
            </h4>
            <p class="text-label text-n-slate-11 mt-0.5">
              {{ data.pace.label }}
            </p>
          </div>
          <span
            class="px-2 py-1 rounded-md text-xs font-semibold tracking-wide uppercase"
            :class="getBadgeColor(data.pace.status)"
          >
            {{ getBadgeLabel(data.pace.status) }}
          </span>
        </div>

        <!-- Repetição por Contato -->
        <div
          class="flex justify-between items-center p-4 bg-n-solid-1 border border-n-weak rounded-lg"
        >
          <div>
            <h4 class="text-body-main font-semibold text-n-slate-12">
              {{ t('INBOX_MGMT.QUALITY_INDEX.REPETITION') }}
            </h4>
            <p class="text-label text-n-slate-11 mt-0.5">
              {{ data.repetition.label }}
            </p>
          </div>
          <span
            class="px-2 py-1 rounded-md text-xs font-semibold tracking-wide uppercase"
            :class="getBadgeColor(data.repetition.status)"
          >
            {{ getBadgeLabel(data.repetition.status) }}
          </span>
        </div>

        <!-- Horário -->
        <div
          class="flex justify-between items-center p-4 bg-n-solid-1 border border-n-weak rounded-lg"
        >
          <div>
            <h4 class="text-body-main font-semibold text-n-slate-12">
              {{ t('INBOX_MGMT.QUALITY_INDEX.TIMING') }}
            </h4>
            <p class="text-label text-n-slate-11 mt-0.5">
              {{ data.timing.label }}
            </p>
          </div>
          <span
            class="px-2 py-1 rounded-md text-xs font-semibold tracking-wide uppercase"
            :class="getBadgeColor(data.timing.status)"
          >
            {{ getBadgeLabel(data.timing.status) }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>
